/* eslint-disable react-hooks/exhaustive-deps */
import moment from 'moment';
import { message, Table } from 'antd';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Header } from "datastake-daf/dist/components";
import { usePagination } from "datastake-daf/dist/hooks";
import { defaultFilters } from "datastake-daf/src/helpers/Table";
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import TableFilters from '../projects/tableFilters';
import TablePagination from '../../Table/pagination';
import getColumns, { emptyDataSource } from './columns';
import CreateUserDrawer from './components/CreateUserDrawer';
import { isEmptyOrSpaces } from '../../../helpers/StringHelper';
import { actions } from '../../../store/reducers/authentication';
import SubjectTableLayout from '../../Layout/SubjectTableLayout';
import { renderBreadCrumbs } from '../../../helpers/breadCrumbs';
import { usePermissions } from '../../../permissions/usePermissions';
import { USER_STATUS, getFiltersConfig, useUserRoles } from './config';
import { getLocales } from '../../Table/newTable';
import { getApplication } from '../../../services/BaseService';

function SubUsers({
  authentication: { invitedUsers, user },
  getInvitedUsers,
  inviteUser,
  mod,
  editUser,
  resendInvite: resendInviteAction,
  removeUser: removeUserAction,
}) {
  const goTo = useNavigate();
  const { t } = useTranslation();
  const [activeFilters, setActiveFilters] = useState({});
  const [search, setSearch] = useState('');
  const { canEdit, canCreate, canDelete } = usePermissions({ permission: "users", defaultValue: {} });
  const [perPage, setPerPage] = useState(defaultFilters.pageSize);
  const [userToEdit, setUserToEdit] = useState(null);
  const [loading, setLoading] = useState(false);

  const app = getApplication();

  const maxSubUsers = user?.company?.maxSubUsers || 10;

  const resendInvite = useCallback((id) => {
    resendInviteAction(id, () => {
      message.success(t('Invite successfully sent'));
    });
  }, [t]);

  const _users = useMemo(() => {
    let _filtered = invitedUsers || [];

    if (!isEmptyOrSpaces(search)) {
      _filtered = _filtered.filter((v) => (`${v.firstName} ${v.lastName}`).toLowerCase().indexOf(search.toLowerCase()) !== -1);
    }

    if (!isEmptyOrSpaces(activeFilters?.email || '')) {
      _filtered = _filtered.filter((v) => (v.email || '').toLowerCase().indexOf(activeFilters.email.toLowerCase()) !== -1);
    }

    if (activeFilters.lastLogin) {
      _filtered = _filtered.filter((v) => {
        const lastLogin = moment(v.lastLogin);

        if (activeFilters.lastLogin.endDate && activeFilters.lastLogin.startDate) {
          return !!(activeFilters.lastLogin.endDate.isAfter(lastLogin) && activeFilters.lastLogin.startDate.isBefore(lastLogin));
        }

        if (activeFilters.lastLogin.endDate) {
          return !!(activeFilters.lastLogin.endDate.isAfter(lastLogin));
        }

        if (activeFilters.lastLogin.startDate) {
          return !!(activeFilters.lastLogin.startDate.isBefore(lastLogin));
        }

        return true;
      });
    }

    if (activeFilters.status) {
      if (activeFilters.status === USER_STATUS.ACTIVE.value) {
        _filtered = _filtered.filter((v) => !v.invitationToken);
      } else if (activeFilters.status === USER_STATUS.pending.value) {
        _filtered = _filtered.filter((v) => v.invitationToken);
      }
    }

    if (activeFilters.userRole) {
      _filtered = _filtered.filter((v) => v.role === activeFilters.userRole);
    }

    return _filtered.map((user) => {
      if (user.apps) {
        const first = (user?.apps || {})[app] || Object.values(user.apps)[0];

        if (first) {
          return { ...user, role: first.role };
        }
      }

      return user;
    });
  }, [search, activeFilters, invitedUsers]);

  const total = useMemo(() => _users.length, [_users]);
  const userRoles = useUserRoles();

  const {
    goPrev,
    goNext,
    canGoNext,
    canGoPrev,
    page,
    setPage,
  } = usePagination({ total, perPage });

  const removeUser = useCallback((id) => {
    removeUserAction(id, () => {
      setPage(1);
      // getInvitedUsers();
    });
  }, [getInvitedUsers]);

  const [createVisible, setCreateVisible] = useState(false);
  const columns = useMemo(() =>
    getColumns({
      t, canEdit, canDelete, removeUser, resendInvite, setUserToEdit,
      userRoles, company: user?.company, canCreate,
      app,
    }),
    [t, canEdit, removeUser, resendInvite, canDelete, userRoles, user, canCreate, app]);

  useEffect(() => {
    setLoading(true);
    getInvitedUsers(() => {
      setLoading(false);
    });
  }, [])

  const filtersInit = useMemo(() => getFiltersConfig({ t, userRoles }), [t, userRoles]);

  const _filtered = useMemo(() => {
    const toReturn = _users.slice((page - 1) * perPage, page * perPage)

    if (perPage === emptyDataSource.length) {
      return emptyDataSource.map((empty, i) => toReturn[i] || empty);
    }

    return toReturn;
  }, [_users, page, perPage]);

  const canInviteMoreUsers = canCreate && invitedUsers.length < maxSubUsers;
  // const canInviteMoreUsers = canCreate;
  const breadCrumbs = useMemo(() =>
    renderBreadCrumbs({ t, view: 'users', goTo, mod }), [t, goTo]);

  return (
    <SubjectTableLayout
      header={(
        <Header
          title={t("Users")}
          breadcrumbs={breadCrumbs}
          actionButtons={canInviteMoreUsers ? [
            {
              type: "primary",
              onClick: () => setCreateVisible(true),
              label: t("New"),
            },
          ] : []} />
      )}
      className="no-tabs"
    >
      <TableFilters
        injectCountry={false}
        setActiveFilters={setActiveFilters}
        t={t}
        search={search}
        setSearch={setSearch}
        filtersInit={filtersInit}
      />
      <div className='daf-table-wrapper'>
        <Table
          locale={getLocales(t)}
          columns={columns}
          size="small"
          rowKey="id"
          loading={loading}
          pagination={false}
          dataSource={_filtered}>
        </Table>
        <TablePagination
          t={t}
          onChangePagination={(v) => {
            setPerPage(v);
            setPage(1);
          }}
          page={page}
          totalPages={total}
          goPrev={goPrev}
          goNext={goNext}
          canGoNext={canGoNext}
          canGoPrev={canGoPrev}
          loaded
          totalItems={total}
          doTotalItems
          perPage={perPage}
        />
      </div>
      <CreateUserDrawer
        mod={mod}
        userRoles={userRoles}
        onInvite={(data) => {
          if (userToEdit) {
            editUser(data, () => {
              setCreateVisible(false);
              setUserToEdit(null);
              getInvitedUsers();
            });
            return;
          }

          inviteUser({ ...data, module: mod }, (data) => {
            if (data && data.link) {
              if (navigator.clipboard) {
                navigator.clipboard.writeText(data.link);
              }
              setCreateVisible(false);
              message.success({ content: t("The invitation was successfully sent.") });
              getInvitedUsers();
            }
          });
        }}
        userToEdit={userToEdit}
        t={t}
        createVisible={createVisible || !!userToEdit}
        onClose={() => {
          setCreateVisible(false);
          setUserToEdit(null);
        }}
      />
    </SubjectTableLayout>
  )
};

const mstp = (state) => ({ authentication: state.authentication });

export default connect(mstp, actions)(SubUsers);
